import { addDays } from 'date-fns';
import type { DateRangePreset } from '@/lib/utils/dateRange';
import { DATE_RANGE_TYPES } from '@/lib/constants/dateFormats';

/**
 * Appointment type constants
 */
export const APPOINTMENT_TYPES = {
    BLOCKED: 'blocked',
    GOOGLE_MEET: 'Google Meet',
    BOOKING: 'booking',
} as const;

export type AppointmentType = (typeof APPOINTMENT_TYPES)[keyof typeof APPOINTMENT_TYPES];

/**
 * Default time values in minutes from midnight
 */
export const DEFAULT_TIME_VALUES = {
    BLOCK_START: 540, // 9:00 AM
    BLOCK_END: 570, // 9:30 AM (9:00 AM + 30 minutes)
    RESCHEDULE_START: 540, // 9:00 AM
    RESCHEDULE_END: 570, // 9:30 AM (9:00 AM + 30 minutes)
} as const;

/**
 * Default appointment duration in minutes
 */
export const DEFAULT_APPOINTMENT_DURATION_MINUTES = 30;

/**
 * Per request, appointments are forward-looking so added reverse filters
 */
export const APPOINTMENT_DATE_PRESETS: DateRangePreset[] = [
    {
        label: 'Next 7 days',
        value: 'next-7d',
        rangeType: DATE_RANGE_TYPES.NEXT_7_DAYS,
        getDateRange: () => {
            const today = new Date();
            return {
                from: today,
                to: addDays(today, 7),
            };
        },
    },
    {
        label: 'Next 14 days',
        value: 'next-14d',
        rangeType: DATE_RANGE_TYPES.NEXT_14_DAYS,
        getDateRange: () => {
            const today = new Date();
            return {
                from: today,
                to: addDays(today, 14),
            };
        },
    },
    {
        label: 'Next 30 days',
        value: 'next-30d',
        rangeType: DATE_RANGE_TYPES.NEXT_30_DAYS,
        getDateRange: () => {
            const today = new Date();
            return {
                from: today,
                to: addDays(today, 30),
            };
        },
    },
    {
        label: 'Next 90 days',
        value: 'next-90d',
        rangeType: DATE_RANGE_TYPES.NEXT_90_DAYS,
        getDateRange: () => {
            const today = new Date();
            return {
                from: today,
                to: addDays(today, 90),
            };
        },
    },
];

export const FILTER_CONFIGS = [
    {
        id: 'title',
        label: 'Title',
        type: 'text' as const,
        placeholder: 'Search by title...',
    },
    {
        id: 'date',
        label: 'Date',
        type: 'date' as const,
        customDatePresets: APPOINTMENT_DATE_PRESETS,
    },
    {
        id: 'status',
        label: 'Status',
        type: 'select' as const,
        customDropdownReduce: 'value',
        customDropdownOptions: [
            { value: 'booked', label: 'Booked' },
            { value: 'canceled', label: 'Canceled' },
        ],
    },
];
